import { BASE_URL } from "@/constants/endpoints";
import { createAxiosWithAuth } from "@/utils/axios-config";
import { objectToQueryStringWithArray } from "@/utils/query-string";
import type { AxiosInstance } from "axios";

let productiveEntitiesApiPromise: Promise<AxiosInstance> | null = null;

const getProductiveEntitiesApi = () => {
  if (productiveEntitiesApiPromise) return productiveEntitiesApiPromise;
  productiveEntitiesApiPromise = createAxiosWithAuth(BASE_URL, "jwt");
  return productiveEntitiesApiPromise;
};

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  statusCode: number;
}

const unwrapApiResponse = <T,>(payload: unknown): T => {
  if (payload && typeof payload === "object" && "data" in payload) {
    return (payload as ApiResponse<T>).data;
  }
  return payload as T;
};

export interface ZonaProductiva {
  id: number;
  nombre: string;
  zonaComercial: string;
}

export interface GetZonasProductivasActivasProps {
  zonaComercial?: string;
}

export const getZonasProductivasActivas = async (
  props: GetZonasProductivasActivasProps = {},
): Promise<ZonaProductiva[]> => {
  const api = await getProductiveEntitiesApi();
  if (!api) return [];

  const qs = objectToQueryStringWithArray(props);
  const url = qs ? `/ZonasProductivas/activas?${qs}` : "/ZonasProductivas/activas";

  const response = await api.get(url);
  return unwrapApiResponse<ZonaProductiva[]>(response.data) ?? [];
};

export interface GranoProductivo {
  id: number;
  nombre: string;
  aplicaPPMB: boolean;
  sumaHA: boolean;
  precioPizarra?: number;
}

export interface GetGranosProductivosProps {
  AplicaPPMB?: boolean;
}

export const getGranosProductivos = async (
  props: GetGranosProductivosProps = {},
): Promise<GranoProductivo[]> => {
  const api = await getProductiveEntitiesApi();
  if (!api) return [];

  const qs = objectToQueryStringWithArray(props);
  const url = qs ? `/GranosProductivos?${qs}` : "/GranosProductivos";

  const response = await api.get(url);
  return unwrapApiResponse<GranoProductivo[]>(response.data) ?? [];
};

export interface CampanaProductiva {
  id: number;
  nombre: string;
}

export const getCampanasProductivasActivas = async (): Promise<
  CampanaProductiva[]
> => {
  const api = await getProductiveEntitiesApi();
  if (!api) return [];

  const response = await api.get(`/CampanasProductivas/activas`);
  return unwrapApiResponse<CampanaProductiva[]>(response.data) ?? [];
};

 export interface GetMargenBrutoZonalProps {
   IdZonaProductiva?: number;
   IdGranoProductivo?: number;
   IdCampProd?: number;
 }

 export interface MargenBrutoZonal {
   id: number;
   idZonaProductiva: number;
   idGranoProductivo: number;
   idCampProd: number;
   cotizacionDolar: number;
   precioCereal: number;
   rindeEstimadoZonal: number;
   precioAgroquimicos: number;
   precioFertilizantes: number;
   precioSemillas: number;
   precioLabores: number;
   precioSeguros: number;
   precioAlquiler: number;
   gastoDeCosecha: number;
   inversionTotal: number;
   rentabilidad: number;
 }

export const getMargenBrutoZonal = async (
  props: GetMargenBrutoZonalProps = {},
): Promise<MargenBrutoZonal | null> => {
  const api = await getProductiveEntitiesApi();
  if (!api) return null;

  const qs = objectToQueryStringWithArray(props);
  const baseUrl = "/MargenBrutoZonal";
  const url = qs ? `${baseUrl}?${qs}` : baseUrl;

  const response = await api.get(url);
  return unwrapApiResponse<MargenBrutoZonal>(response.data) ?? null;
};


export const getAllCampanasProductivas = async (): Promise<CampanaProductiva[]> => {
  const api = await getProductiveEntitiesApi();
  if (!api) return [];

  try {
    const response = await api.get("/CampanasProductivas");
    return unwrapApiResponse<CampanaProductiva[]>(response.data) ?? [];
  } catch (error) {
    console.error("Error in getAllCampanasProductivas:", error);
    return [];
  }
};